import {useApiManager} from "../../../components/utils/api";
import {
	EmployeeVacationList,
	EmpVacationRestControllerApiFactory,
	VacationListStateEnum,
	VacationRequest,
	VacationRequestTypeEnum,
} from "arteco-api-client-ts";
import React, {useEffect, useState} from "react";
import {
	Button,
	Grid,
	LinearProgress,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	useMediaQuery,
} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import BeachAccessRoundedIcon from "@mui/icons-material/BeachAccessRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import EventBusyRoundedIcon from "@mui/icons-material/EventBusyRounded";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import {Link as RouterLink} from "react-router-dom";
import {dateFromToString} from "../../../components/utils/dates";
import {FormContent} from "../../../components/form/form_inputs/form_buttons";
import {useForm, UseFormProps} from "../../../components/form/form_model";
import {FormInput} from "../../../components/form/form_inputs/form_input";
import FormSelect, {FormSelectItem} from "../../../components/form/form_inputs/form_select";
import {TypeConf, types} from "../../../components/form/types";
import {
	btnSx,
	EmptyState,
	ListCard,
	Page,
	PageHeader,
	Panel,
	SkeletonRows,
	SoftChip,
	StatGrid,
	StatTile,
	tableContainerSx,
	tableSx,
} from "../../../components/kit";
import {VACATION_STATE, vacationTypeLabel} from "./labels";

/** El mismo selector de tipo que el resto de formularios, pero con la etiqueta en español. */
const vacationTypeEs: TypeConf<FormSelectItem> = {
	...(types.vacationType as TypeConf<FormSelectItem>),
	labeler: (bean) => vacationTypeLabel(String(bean.value)) || bean.label,
};

/**
 * Ausencias del empleado (#13877): lo gastado del año frente a lo disponible, una solicitud nueva y
 * las solicitudes con su estado. Las de tipo especial piden el justificante en «Tus documentos».
 */
export const EmployeeVacationPage = () => {
	const apiManager = useApiManager();
	const vacationApi = apiManager.factory(EmpVacationRestControllerApiFactory);
	const [vacations, setVacations] = useState<EmployeeVacationList>();
	const theme = useTheme();
	const compact = useMediaQuery(theme.breakpoints.down("md"));

	const loadVacations = () => {
		apiManager.executeRaw<EmployeeVacationList>(vacationApi.getEmployeeVacations())
			.then((resp) => setVacations(resp.data));
	};

	useEffect(() => {
		loadVacations();
	}, []);

	const vacationForm = useForm({
		updater: (bean) => vacationApi.newEmployeeVacationRequest(bean),
		factory: () => {
			return {
				type: VacationRequestTypeEnum.HOLIDAY
			} as VacationRequest;
		},
		postUpdate: (bean) => loadVacations(),
		preUpdate: bean => {
			if (bean.type != "HOLIDAY" && bean.type != "GIFT" && bean.type != "UNPAID_LEAVE") {
				return confirm("Recuerda anexar el documento justificativo en la sección de \"Tus Documentos\"");
			}
			return true;
		}
	} as UseFormProps<VacationRequest>);

	const taken = vacations?.taken || 0;
	const allowed = vacations?.allowed || 0;
	const left = allowed - taken;
	const list = vacations?.vacations || [];
	const pending = list.filter(v => v.state === VacationListStateEnum.PENDING).length;

	return (
		<Page>
			<PageHeader title={"Ausencias " + new Date().getFullYear()}
						subtitle="Pide vacaciones y permisos, y consulta en qué estado están."
						actions={<Button component={RouterLink} to="/app/employee/holidays" variant="outlined"
										 startIcon={<EventRoundedIcon/>} sx={btnSx}>Festivos del año</Button>}/>

			<StatGrid>
				<StatTile icon={<BeachAccessRoundedIcon fontSize="small"/>} tone="info" loading={!vacations}
						  label="Días disfrutados" value={<>{taken} <Typography component="span" color="text.secondary">de {allowed}</Typography></>}
						  footer={vacations && (
							  <LinearProgress variant="determinate" value={allowed ? Math.min(100, taken * 100 / allowed) : 0}
											  sx={{mt: 1, height: 6, borderRadius: 3, "& .MuiLinearProgress-bar": {borderRadius: 3}}}/>
						  )}/>
				<StatTile icon={<EventAvailableRoundedIcon fontSize="small"/>} tone={left > 0 ? "success" : "neutral"} loading={!vacations}
						  label="Te quedan" value={left}/>
				<StatTile icon={<EventBusyRoundedIcon fontSize="small"/>} tone={pending ? "warning" : "neutral"} loading={!vacations}
						  label="Solicitudes pendientes" value={pending}/>
			</StatGrid>

			<Grid container spacing={3}>
				<Grid item xs={12} md={5}>
					<Panel title="Nueva solicitud"
						   subtitle="Desde y hasta, ambos incluidos; para un solo día, el mismo en los dos. Los fines de semana y festivos no cuentan.">
						{/* El formulario de siempre, con su validación y sus mensajes de error. */}
						<FormContent form={vacationForm} withoutPaper hideTopButtons={true}>
							<Grid container spacing={2}>
								<Grid item xs={6}>
									<FormInput field={"periodFrom"} label={"Desde"} type={"date"}/>
								</Grid>
								<Grid item xs={6}>
									<FormInput field={"periodTo"} label={"Hasta"} type={"date"}/>
								</Grid>
								<Grid item xs={12}>
									<FormSelect field={"type"} label={"Tipo"} type={vacationTypeEs}/>
								</Grid>
							</Grid>
							<FormInput field={"title"} label={"Comentario"}/>
						</FormContent>
					</Panel>
				</Grid>

				<Grid item xs={12} md={7}>
					<Panel title="Tus solicitudes" action={vacations && list.length > 0 &&
						<Typography variant="body2" color="text.secondary">{list.length}</Typography>}>
						{!vacations ? <SkeletonRows/> : list.length === 0 ? (
							<EmptyState icon={<BeachAccessRoundedIcon/>} title="Aún no has pedido ninguna ausencia este año."/>
						) : compact ? (
							<Stack spacing={1.5}>
								{list.map((v, idx) => {
									const st = VACATION_STATE[v.state] || {label: v.state, tone: "neutral" as const};
									return (
										<ListCard key={idx}>
											<Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
												<Typography variant="body2" fontWeight={600}>
													{dateFromToString(v.periodFrom)} – {dateFromToString(v.periodTo)}
												</Typography>
												<SoftChip label={st.label} tone={st.tone}/>
											</Stack>
											<Typography variant="caption" color="text.secondary" display="block" sx={{mt: 0.5}}>
												{vacationTypeLabel(v.type)}{v.title ? " · " + v.title : ""}
											</Typography>
										</ListCard>
									);
								})}
							</Stack>
						) : (
							<TableContainer sx={tableContainerSx}>
								<Table size="small" sx={tableSx}>
									<TableHead>
										<TableRow>
											<TableCell>Periodo</TableCell>
											<TableCell>Tipo</TableCell>
											<TableCell>Comentario</TableCell>
											<TableCell align="right">Estado</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{list.map((v, idx) => {
											const st = VACATION_STATE[v.state] || {label: v.state, tone: "neutral" as const};
											return (
												<TableRow key={idx}>
													<TableCell sx={{whiteSpace: "nowrap"}}>
														<Typography variant="body2" fontWeight={600}>
															{dateFromToString(v.periodFrom)} – {dateFromToString(v.periodTo)}
														</Typography>
													</TableCell>
													<TableCell sx={{whiteSpace: "nowrap"}}>{vacationTypeLabel(v.type)}</TableCell>
													<TableCell>
														<Typography variant="body2" color="text.secondary" sx={{overflowWrap: "anywhere"}}>
															{v.title || "—"}
														</Typography>
													</TableCell>
													<TableCell align="right"><SoftChip label={st.label} tone={st.tone}/></TableCell>
												</TableRow>
											);
										})}
									</TableBody>
								</Table>
							</TableContainer>
						)}
					</Panel>
				</Grid>
			</Grid>
		</Page>
	);
};
